// Firebase imports
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toDRXError } from '@/lib/drx-error';
import { ICartRepository } from './interfaces';

// Cart entity interfaces
export interface CreateCartData {
  userId: string;
  session_id?: string;
  status: 'active' | 'abandoned' | 'completed';
  totalAmount: number;
  currency: string;
  discountAmount?: number;
  taxAmount?: number;
  shippingAmount?: number;
  expiresAt?: Date;
}

export interface UpdateCartData {
  status?: 'active' | 'abandoned' | 'completed';
  totalAmount?: number;
  discountAmount?: number;
  taxAmount?: number;
  shippingAmount?: number;
  expiresAt?: Date;
  updatedAt?: Timestamp;
}

export interface CartFilter {
  userId?: string;
  status?: 'active' | 'abandoned' | 'completed';
  createdAfter?: Date;
  createdBefore?: Date;
  totalMin?: number;
  totalMax?: number;
}

export interface GetCartData {
  id: string;
  userId: string;
  session_id?: string;
  status: 'active' | 'abandoned' | 'completed';
  totalAmount: number;
  currency: string;
  discountAmount: number;
  taxAmount: number;
  shippingAmount: number;
  expiresAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CreateCartItemData {
  cartId: string;
  productType: 'course' | 'resource' | 'subscription' | 'connect';
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discountAmount?: number;
  metadata?: Record<string, any>;
}

export interface GetCartItemData {
  id: string;
  cartId: string;
  productType: 'course' | 'resource' | 'subscription' | 'connect';
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discountAmount: number;
  metadata?: Record<string, any>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class CartRepository implements ICartRepository {
  constructor() {
    console.log('[CartRepository] Constructor - Using client-side Firebase DB instance:', !!db);
  }

  private collectionName = 'carts';
  private itemsCollectionName = 'cartItems';

  private toRepositoryError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, {
      code,
      devMessage,
    });
  }

  // Create cart
  async createCart(cartData: CreateCartData): Promise<GetCartData> {
    console.log('[CartRepository.createCart] START - CartData:', cartData);
    try {
      console.log('[CartRepository.createCart] Creating cart document in Firestore');
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...cartData,
        discountAmount: cartData.discountAmount || 0,
        taxAmount: cartData.taxAmount || 0,
        shippingAmount: cartData.shippingAmount || 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newCart = await this.getCartById(docRef.id);
      console.log('[CartRepository.createCart] SUCCESS - Created cart with ID:', docRef.id);
      return newCart!;
    } catch (error) {
      console.error('[CartRepository.createCart] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_CREATE_FAILED', 'Failed to create cart');
    }
  }

  // Get cart by ID
  async getCartById(cartId: string): Promise<GetCartData | null> {
    console.log('[CartRepository.getCartById] START - CartId:', cartId);
    try {
      console.log('[CartRepository.getCartById] Fetching cart document from Firestore');
      const docRef = doc(db, this.collectionName, cartId);
      const docSnapshot = await getDocs(query(collection(db, this.collectionName), where('__name__', '==', cartId)));
      
      if (docSnapshot.empty) {
        console.log('[CartRepository.getCartById] Cart not found');
        return null;
      }

      const cartDoc = docSnapshot.docs[0];
      const cart = {
        id: cartDoc.id,
        ...cartDoc.data(),
      } as GetCartData;

      console.log('[CartRepository.getCartById] SUCCESS - Found cart');
      return cart;
    } catch (error) {
      console.error('[CartRepository.getCartById] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_FETCH_FAILED', `Failed to get cart ${cartId}`);
    }
  }

  // Get carts with filters
  async getCarts(filter: CartFilter = {}, limitCount?: number): Promise<GetCartData[]> {
    console.log('[CartRepository.getCarts] START - Filter:', filter, 'LimitCount:', limitCount);
    try {
      console.log('[CartRepository.getCarts] Building Firestore query with filters');
      let q = collection(db, this.collectionName);

      // Build query constraints array
      const constraints = [];

      // Add filters
      if (filter.userId) {
        constraints.push(where('userId', '==', filter.userId));
      }
      if (filter.status) {
        constraints.push(where('status', '==', filter.status));
      }
      if (filter.createdAfter) {
        constraints.push(where('createdAt', '>=', filter.createdAfter));
      }
      if (filter.createdBefore) {
        constraints.push(where('createdAt', '<=', filter.createdBefore));
      }
      if (filter.totalMin) {
        constraints.push(where('totalAmount', '>=', filter.totalMin));
      }
      if (filter.totalMax) {
        constraints.push(where('totalAmount', '<=', filter.totalMax));
      }

      // Add ordering and limit
      constraints.push(orderBy('createdAt', 'desc'));
      if (limitCount) {
        constraints.push(limit(limitCount));
      }

      // Build the final query
      if (constraints.length > 0) {
        q = query(q, ...constraints);
        console.log('[CartRepository.getCarts] Built query with', constraints.length, 'constraints');
      }

      const querySnapshot = await getDocs(q);
      const carts: GetCartData[] = [];

      querySnapshot.forEach((doc) => {
        carts.push({
          id: doc.id,
          ...doc.data(),
        } as GetCartData);
      });

      console.log('[CartRepository.getCarts] SUCCESS - Retrieved', carts.length, 'carts');
      return carts;
    } catch (error) {
      console.error('[CartRepository.getCarts] ERROR:', error);
      throw this.toRepositoryError(error, 'CARTS_FETCH_FAILED', 'Failed to get carts');
    }
  }

  // Get all carts (admin access)
  async getAllCarts(limitCount?: number): Promise<GetCartData[]> {
    console.log('[CartRepository.getAllCarts] START - LimitCount:', limitCount);
    try {
      console.log('[CartRepository.getAllCarts] Retrieving all carts (admin access)');
      const result = await this.getCarts({}, limitCount);
      console.log('[CartRepository.getAllCarts] SUCCESS - Retrieved', result.length, 'carts');
      return result;
    } catch (error) {
      console.error('[CartRepository.getAllCarts] ERROR:', error);
      throw this.toRepositoryError(error, 'CARTS_ALL_FETCH_FAILED', 'Failed to get all carts');
    }
  }

  // Update cart
  async updateCart(cartId: string, updateData: UpdateCartData): Promise<GetCartData> {
    console.log('[CartRepository.updateCart] START - CartId:', cartId, 'UpdateData:', updateData);
    try {
      console.log('[CartRepository.updateCart] Updating cart document in Firestore');
      const docRef = doc(db, this.collectionName, cartId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const updatedCart = await this.getCartById(cartId);
      console.log('[CartRepository.updateCart] SUCCESS - Updated cart');
      return updatedCart!;
    } catch (error) {
      console.error('[CartRepository.updateCart] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_UPDATE_FAILED', `Failed to update cart ${cartId}`);
    }
  }

  // Delete cart
  async deleteCart(cartId: string): Promise<void> {
    console.log('[CartRepository.deleteCart] START - CartId:', cartId);
    try {
      console.log('[CartRepository.deleteCart] Deleting cart and its items');
      
      // Delete all cart items first
      await this.deleteAllCartItems(cartId);

      // Delete the cart
      const docRef = doc(db, this.collectionName, cartId);
      await deleteDoc(docRef);
      console.log('[CartRepository.deleteCart] SUCCESS - Deleted cart and related data');
    } catch (error) {
      console.error('[CartRepository.deleteCart] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_DELETE_FAILED', `Failed to delete cart ${cartId}`);
    }
  }

  // Get user's active cart
  async getUserActiveCart(userId: string): Promise<GetCartData | null> {
    console.log('[CartRepository.getUserActiveCart] START - UserId:', userId);
    try {
      console.log('[CartRepository.getUserActiveCart] Finding active cart for user');
      const filter: CartFilter = { userId: userId, status: 'active' };
      const carts = await this.getCarts(filter, 1);
      
      const activeCart = carts.length > 0 ? carts[0] : null;
      console.log('[CartRepository.getUserActiveCart] SUCCESS - Found active cart:', !!activeCart);
      return activeCart;
    } catch (error) {
      console.error('[CartRepository.getUserActiveCart] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_ACTIVE_FETCH_FAILED', `Failed to get active cart for user ${userId}`);
    }
  }

  // ===== CART ITEMS METHODS =====

  // Add item to cart
  async addCartItem(itemData: CreateCartItemData): Promise<GetCartItemData> {
    console.log('[CartRepository.addCartItem] START - ItemData:', itemData);
    try {
      console.log('[CartRepository.addCartItem] Adding item to cart');
      const docRef = await addDoc(collection(db, this.itemsCollectionName), {
        ...itemData,
        discountAmount: itemData.discountAmount || 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newItem = await this.getCartItemById(docRef.id);
      console.log('[CartRepository.addCartItem] SUCCESS - Added cart item with ID:', docRef.id);
      return newItem!;
    } catch (error) {
      console.error('[CartRepository.addCartItem] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_ITEM_CREATE_FAILED', `Failed to add item to cart ${itemData.cartId}`);
    }
  }

  // Get cart item by ID
  async getCartItemById(itemId: string): Promise<GetCartItemData | null> {
    console.log('[CartRepository.getCartItemById] START - ItemId:', itemId);
    try {
      const docSnapshot = await getDocs(query(collection(db, this.itemsCollectionName), where('__name__', '==', itemId)));
      
      if (docSnapshot.empty) {
        console.log('[CartRepository.getCartItemById] Cart item not found');
        return null;
      }

      const itemDoc = docSnapshot.docs[0];
      const item = {
        id: itemDoc.id,
        ...itemDoc.data(),
      } as GetCartItemData;

      console.log('[CartRepository.getCartItemById] SUCCESS - Found cart item');
      return item;
    } catch (error) {
      console.error('[CartRepository.getCartItemById] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_ITEM_FETCH_FAILED', `Failed to get cart item ${itemId}`);
    }
  }

  // Get all items in a cart
  async getCartItems(cartId: string): Promise<GetCartItemData[]> {
    console.log('[CartRepository.getCartItems] START - CartId:', cartId);
    try {
      console.log('[CartRepository.getCartItems] Fetching all items for cart');
      const q = query(
        collection(db, this.itemsCollectionName),
        where('cartId', '==', cartId),
        orderBy('createdAt', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const items: GetCartItemData[] = [];

      querySnapshot.forEach((doc) => {
        items.push({
          id: doc.id,
          ...doc.data(),
        } as GetCartItemData);
      });

      console.log('[CartRepository.getCartItems] SUCCESS - Retrieved', items.length, 'cart items');
      return items;
    } catch (error) {
      console.error('[CartRepository.getCartItems] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_ITEMS_FETCH_FAILED', `Failed to get items for cart ${cartId}`);
    }
  }

  // Update cart item
  async updateCartItem(itemId: string, updateData: Partial<CreateCartItemData>): Promise<GetCartItemData> {
    console.log('[CartRepository.updateCartItem] START - ItemId:', itemId, 'UpdateData:', updateData);
    try {
      const docRef = doc(db, this.itemsCollectionName, itemId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const updatedItem = await this.getCartItemById(itemId);
      console.log('[CartRepository.updateCartItem] SUCCESS - Updated cart item');
      return updatedItem!;
    } catch (error) {
      console.error('[CartRepository.updateCartItem] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_ITEM_UPDATE_FAILED', `Failed to update cart item ${itemId}`);
    }
  }

  // Remove item from cart
  async removeCartItem(itemId: string): Promise<void> {
    console.log('[CartRepository.removeCartItem] START - ItemId:', itemId);
    try {
      const docRef = doc(db, this.itemsCollectionName, itemId);
      await deleteDoc(docRef);
      console.log('[CartRepository.removeCartItem] SUCCESS - Removed cart item');
    } catch (error) {
      console.error('[CartRepository.removeCartItem] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_ITEM_DELETE_FAILED', `Failed to remove cart item ${itemId}`);
    }
  }

  // Delete all cart items
  async deleteAllCartItems(cartId: string): Promise<void> {
    console.log('[CartRepository.deleteAllCartItems] START - CartId:', cartId);
    try {
      console.log('[CartRepository.deleteAllCartItems] Getting all items for cart before deletion');
      const items = await this.getCartItems(cartId);
      console.log('[CartRepository.deleteAllCartItems] Deleting', items.length, 'items');
      
      const deletePromises = items.map(item =>
        deleteDoc(doc(db, this.itemsCollectionName, item.id))
      );
      await Promise.all(deletePromises);
      console.log('[CartRepository.deleteAllCartItems] SUCCESS - Deleted all cart items');
    } catch (error) {
      console.error('[CartRepository.deleteAllCartItems] ERROR:', error);
      throw this.toRepositoryError(error, 'CART_ITEMS_DELETE_FAILED', `Failed to delete items for cart ${cartId}`);
    }
  }
}
